import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Button } from "../ui/button";

export function SectionHeading({ eyebrow, title, accent, description, align = "center" }) {
  return (
    <div
      className={`max-w-3xl ${
        align === "center" ? "mx-auto text-center" : "text-left"
      } mb-10 md:mb-14`}
    >
      {eyebrow && (
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-3">
          <span className="w-6 h-[2px] bg-[#f59321]" />
          {eyebrow}
        </div>
      )}
      <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0f2847] leading-[1.1]">
        {title}{" "}
        {accent && <span className="text-[#f59321]">{accent}</span>}
      </h2>
      {description && (
        <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
          {description}
        </p>
      )}
    </div>
  );
}

export function ServiceCard({ service, variant = "default" }) {
  const Icon = Icons[service.icon] || Icons.Package;
  if (variant === "detailed") {
    return (
      <div className="card-lift group bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col">
        <div className="img-zoom h-52 bg-slate-100">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="p-6 flex-1 flex flex-col">
          <div className="w-12 h-12 rounded-xl bg-[#0f2847] text-white flex items-center justify-center mb-4 group-hover:bg-[#f59321] transition">
            <Icon className="w-6 h-6" />
          </div>
          <h3 className="font-display text-xl font-bold text-[#0f2847] mb-2">
            {service.title}
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-4">{service.short}</p>
          <ul className="space-y-2 mb-5">
            {service.details.map((d, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <Icons.CheckCircle2 className="w-4 h-4 text-[#f59321] mt-0.5 shrink-0" />
                {d}
              </li>
            ))}
          </ul>
          <Link to="/contact" className="mt-auto">
            <Button
              variant="ghost"
              className="px-0 hover:bg-transparent text-[#0f2847] font-semibold hover:text-[#f59321]"
            >
              Book this service
              <Icons.ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className="card-lift group bg-white rounded-2xl border border-slate-200 p-6 md:p-7 flex flex-col h-full">
      <div className="w-14 h-14 rounded-2xl bg-[#0f2847] text-white flex items-center justify-center mb-5 group-hover:bg-[#f59321] transition-colors">
        <Icon className="w-7 h-7" />
      </div>
      <h3 className="font-display text-xl font-bold text-[#0f2847] mb-2">
        {service.title}
      </h3>
      <p className="text-sm text-slate-600 leading-relaxed flex-1">{service.short}</p>
      <Link
        to="/services"
        className="mt-5 inline-flex items-center text-sm font-bold text-[#0f2847] hover:text-[#f59321] transition-colors"
      >
        Learn more
        <Icons.ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
      </Link>
    </div>
  );
}

export function FeatureCard({ icon, title, text }) {
  const Icon = Icons[icon] || Icons.Star;
  return (
    <div className="card-lift bg-white rounded-2xl border border-slate-200 p-6 flex gap-4">
      <div className="w-12 h-12 rounded-xl bg-[#f59321]/10 text-[#f59321] flex items-center justify-center shrink-0">
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <h4 className="font-display font-bold text-[#0f2847] mb-1">{title}</h4>
        <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

export function StatPill({ value, label }) {
  return (
    <div className="flex flex-col">
      <div className="font-display text-3xl md:text-4xl font-extrabold text-[#0f2847] leading-none">
        {value}
      </div>
      <div className="mt-1 text-xs md:text-sm text-slate-500 font-medium uppercase tracking-wider">
        {label}
      </div>
    </div>
  );
}

export function PageHeader({ eyebrow, title, accent, description, image }) {
  return (
    <section className="relative bg-[#0f2847] text-white overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        {image && (
          <img src={image} alt="" className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f2847] via-[#0f2847]/85 to-[#0f2847]/40" />
      </div>
      <div className="absolute top-1/2 left-0 w-full speed-line" style={{ width: "40%" }} />
      <div className="container-518 relative py-20 md:py-28">
        <div className="max-w-3xl">
          {eyebrow && (
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-4">
              <span className="w-6 h-[2px] bg-[#f59321]" />
              {eyebrow}
            </div>
          )}
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
            {title} {accent && <span className="text-[#f59321]">{accent}</span>}
          </h1>
          {description && (
            <p className="mt-5 text-lg text-slate-200 leading-relaxed max-w-2xl">
              {description}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
